import { useState, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { IconChevronDown } from "@tabler/icons-react";
import { useFleetSteeringMeasurements } from "@/api/hooks/use-fleet-measurements";
import { useEvent } from "@/api/hooks";
import { TeamFlag } from "@/components/team-flag";
import { timeAgo } from "@/lib/time";
import { asEventId, type EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";
import { CheckedCard, ComparisonTeamPills, teamColor, useToggleSet, type ChartTooltip } from "./_shared";

type FleetSteeringTeamEntry = components["schemas"]["FleetSteeringTeamEntry"];

const STEERING_RANGE = 5; // ±5°

function SteeringSparkline({ history }: { history: FleetSteeringTeamEntry["history"] }) {
  const w = 140, h = 20, px = 6, py = 3;
  if (!history.length) return null;

  const xf = (i: number) => history.length === 1 ? w / 2 : px + (i / (history.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v + STEERING_RANGE) / (STEERING_RANGE * 2)) * (h - py * 2);
  const centerY = yf(0);

  const vals = history.map((e) => e.rudder_angle_offset);

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block w-full">
      <line x1={px} y1={centerY} x2={w - px} y2={centerY} stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2,2" />
      {history.length === 1 ? (
        <circle cx={xf(0)} cy={yf(vals[0])} r={2.5} fill="#3b82f6" stroke="var(--card)" strokeWidth={1} />
      ) : (
        <>
          <path d={vals.map((v: number, i: number) => `${i === 0 ? "M" : "L"}${xf(i)},${yf(v)}`).join(" ")} fill="none" stroke="#3b82f6" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          {vals.map((v: number, i: number) => (
            <circle key={i} cx={xf(i)} cy={yf(v)} r={i === vals.length - 1 ? 2.5 : 1.5} fill="#3b82f6" stroke={i === vals.length - 1 ? "var(--card)" : "none"} strokeWidth={1} opacity={i === vals.length - 1 ? 1 : 0.5} />
          ))}
        </>
      )}
    </svg>
  );
}

function SteeringHistoryTable({ entry }: { entry: FleetSteeringTeamEntry }) {
  if (!entry.history.length) return null;
  const reversed = [...entry.history].reverse();

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Event</th>
            <th className="py-1.5 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-center">Rudder Angle Offset</th>
            <th className="py-1.5 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-center">Delta</th>
            <th className="py-1.5 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-right">Fetched</th>
          </tr>
        </thead>
        <tbody>
          {reversed.map((h, ri) => {
            const ci = entry.history.length - 1 - ri;
            const prev = ci > 0 ? entry.history[ci - 1] : null;
            const d = prev ? h.rudder_angle_offset - prev.rudder_angle_offset : null;
            const isLatest = ri === 0;
            return (
              <tr key={h.event_name} className={`${isLatest ? "bg-blue-50/30 dark:bg-blue-950/20" : "hover:bg-muted/30"} border-b border-border/30`}>
                <td className="py-1.5 px-2 text-xs text-muted-foreground font-medium whitespace-nowrap">
                  {h.event_name}
                  {isLatest && <span className="ml-1 text-[9px] text-blue-500 font-semibold uppercase">Latest</span>}
                </td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center font-semibold">{h.rudder_angle_offset.toFixed(2)}°</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-center">
                  {d == null ? "" : Math.abs(d) < 0.005 ? <span className="text-muted-foreground/40">—</span> : (
                    <span className={d > 0 ? "text-amber-500" : "text-blue-500"}>
                      {d > 0 ? "↑" : "↓"}{Math.abs(d).toFixed(2)}°
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-xs text-muted-foreground text-right">{timeAgo(h.fetched_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SteeringComparisonChart({ teams, allEvents }: {
  teams: { code: string; history: FleetSteeringTeamEntry["history"] }[];
  allEvents: string[];
}) {
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const showTooltip = (e: React.MouseEvent, text: string) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 8, text });
  };

  const w = 600, h = 170, px = 40, py = 25;
  const yMin = -STEERING_RANGE, yMax = STEERING_RANGE;
  const xf = (i: number) => allEvents.length === 1 ? w / 2 : px + (i / (allEvents.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v - yMin) / (yMax - yMin)) * (h - py * 2);
  const centerY = yf(0);

  return (
    <div className="relative" onMouseLeave={() => setTooltip(null)}>
    {tooltip && (
      <div className="absolute z-10 px-2 py-1 rounded bg-gray-900 text-white text-[10px] tabular-nums whitespace-nowrap pointer-events-none" style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, -100%)" }}>
        {tooltip.text}
      </div>
    )}
    <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} className="block w-full" preserveAspectRatio="xMidYMid meet">
      {[-4, -2, 0, 2, 4].map((v) => (
        <g key={v}>
          <text x={px - 6} y={yf(v) + 3} textAnchor="end" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{v}°</text>
          <line x1={px} y1={yf(v)} x2={w - px} y2={yf(v)} stroke="var(--border)" strokeWidth={1} />
        </g>
      ))}
      <line x1={px} y1={centerY} x2={w - px} y2={centerY} stroke="#94a3b8" strokeWidth={1} strokeDasharray="5,4" />
      {allEvents.map((ev, i) => (
        <text key={ev} x={xf(i)} y={h - 5} textAnchor="middle" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{ev}</text>
      ))}
      {teams.map(({ code, history }) => {
        const color = teamColor(code);
        const points = history.map((h) => {
          const idx = allEvents.indexOf(h.event_name);
          return idx === -1 ? null : { i: idx, v: h.rudder_angle_offset, ev: h.event_name };
        }).filter(Boolean) as { i: number; v: number; ev: string }[];
        if (!points.length) return null;
        return (
          <g key={code}>
            {points.length > 1 && (
              <path d={points.map((p, j) => `${j ? "L" : "M"}${xf(p.i)},${yf(p.v)}`).join(" ")} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            )}
            {points.map((p, j) => {
              const isLast = j === points.length - 1;
              return (
                <g key={j}>
                  <circle cx={xf(p.i)} cy={yf(p.v)} r={isLast ? 4.5 : 3} fill={color} stroke="var(--card)" strokeWidth={isLast ? 2 : 1.5} style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev}: ${p.v.toFixed(2)}°`)} />
                  {isLast && <text x={xf(p.i) + 7} y={yf(p.v) + 4} fill={color} fontSize={9} fontWeight={600} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{code} {p.v.toFixed(2)}°</text>}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

export function SteeringPage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const eventId = asEventId(rawEventId ?? "") as EventId;
  const { data: event } = useEvent(eventId);
  const { data: steeringTeams, isLoading } = useFleetSteeringMeasurements(eventId);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [comparedTeams, toggleCompare] = useToggleSet<string>();

  const allEvents = useMemo(() => {
    if (!steeringTeams) return [];
    const evMap = new Map<string, string>();
    steeringTeams.forEach((t) => t.history.forEach((h) => { if (!evMap.has(h.event_name)) evMap.set(h.event_name, h.event_date); }));
    return [...evMap.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([name]) => name);
  }, [steeringTeams]);

  const comparedData = useMemo(() => {
    if (!steeringTeams) return [];
    return [...comparedTeams].map((code) => {
      const t = steeringTeams.find((e) => e.team_code === code);
      return { code, history: t?.history ?? [] };
    }).filter((d) => d.history.length > 0);
  }, [comparedTeams, steeringTeams]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Steering" subtitle={event?.name} />
        <div className="mt-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
      </div>
    );
  }
  if (!steeringTeams) return null;

  const checked = steeringTeams.filter((t) => t.latest_value != null).length;
  const values = steeringTeams.filter((t) => t.latest_value != null).map((t) => t.latest_value!);
  const fleetAvg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const maxDev = values.length ? Math.max(...values.map((v) => Math.abs(v))) : null;

  return (
    <div>
      <PageHeader title="Steering" subtitle={event?.name} count={steeringTeams.length} countLabel="teams" />

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <CheckedCard checked={checked} total={steeringTeams.length} />
        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3">
          <div>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Fleet Avg</div>
            <div className="text-lg font-bold tabular-nums">{fleetAvg != null ? `${fleetAvg.toFixed(2)}°` : "—"}</div>
          </div>
        </div>
        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3">
          <div>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Max Deviation</div>
            <div className="text-lg font-bold tabular-nums">{maxDev != null ? `${maxDev.toFixed(2)}°` : "—"}</div>
          </div>
        </div>
      </div>

      {comparedTeams.size > 0 && comparedData.length > 0 && (
        <div className="mt-6 bg-card rounded-lg border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold">Fleet Comparison</span>
              <span className="text-[10px] text-muted-foreground">Rudder angle offset across events</span>
            </div>
            <ComparisonTeamPills
              codes={comparedTeams}
              teamLookup={(code) => steeringTeams.find((e) => e.team_code === code)}
              onRemove={toggleCompare}
            />
          </div>
          <div className="p-4">
            <SteeringComparisonChart teams={comparedData} allEvents={allEvents} />
          </div>
        </div>
      )}

      {/* Team rows */}
      <div className="mt-6 flex flex-col gap-2">
        {steeringTeams.map((t) => {
          const hasData = t.latest_value != null;
          const isExpanded = expandedTeam === t.team_code;
          const isCompared = comparedTeams.has(t.team_code);
          const borderClass = hasData ? "border-l-blue-400" : "border-l-border";

          return (
            <div key={t.team_code} className={`border-l-3 rounded-r-lg bg-card ${borderClass}`}>
              <div
                className={`flex items-center pl-4 pr-5 py-3 transition-shadow cursor-pointer hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)] ${isExpanded ? "shadow-[0_1px_4px_rgba(0,0,0,0.04)]" : ""}`}
                onClick={() => hasData && setExpandedTeam(isExpanded ? null : t.team_code)}
              >
                <div className="flex items-center gap-3 w-28 shrink-0">
                  <div className="w-4 flex items-center justify-center shrink-0">
                    {hasData && (
                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-border accent-blue-500 cursor-pointer"
                        checked={isCompared}
                        onChange={() => toggleCompare(t.team_code)}
                        onClick={(e) => e.stopPropagation()} />
                    )}
                  </div>
                  <TeamFlag iso_alpha2={t.team_iso_alpha2} size="xs" />
                  <span className="font-medium text-[13px]">{t.team_code}</span>
                </div>

                <div className="flex-1 min-w-0 px-3">
                  {hasData ? (
                    <span className="text-sm font-semibold tabular-nums">{t.latest_value!.toFixed(2)}°</span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">No steering data</span>
                  )}
                </div>

                <div className="w-[180px] shrink-0 px-2 hidden md:block">
                  {t.history.length > 0 ? <SteeringSparkline history={t.history} /> : <span className="text-[10px] text-muted-foreground italic">No history</span>}
                </div>

                <div className="flex items-center gap-2 w-24 shrink-0 justify-end">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{hasData && t.latest_fetched_at ? timeAgo(t.latest_fetched_at) : "—"}</span>
                  {hasData && <IconChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />}
                </div>
              </div>

              {isExpanded && hasData && (
                <div className="border-t border-border/50 px-4 py-3">
                  <SteeringHistoryTable entry={t} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
